import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Navbar } from "../../components/Navbar";
import { LanguageToggle } from "../../components/LanguageToggle";
import { Title } from "../../components/Title";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { InfoBox } from "../../components/InfoBox";
import { Page } from "../../components/Page";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { useCompleteShopifyOAuthMutation } from "../../query/shopifyIntegrationQuery";
import { captureException } from "@sentry/react";

const buildShopifyOAuthCallback = (
  searchParams: URLSearchParams,
): { code: string; shop: string; state: string } | null => {
  const code = searchParams.get("code");
  const shop = searchParams.get("shop");
  const state = searchParams.get("state");
  if (!code || !shop || !state) {
    return null;
  }

  return { code, shop, state };
};

const ContentWrapper = styled.div`
  max-width: 500px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
  text-align: center;
`;

const Message = styled.p`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
`;

const ShopifyIntegration = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { mutate, isError, error, isPending, isSuccess } =
    useCompleteShopifyOAuthMutation();
  const processedCallbacksRef = useRef(new Set<string>());

  const errorMessage = useMemo(() => {
    if (isError) {
      return error?.message || t("shopify.unknownError");
    }
    return t("shopify.unknownError");
  }, [isError, error, t]);

  useEffect(() => {
    if (isError) {
      return;
    }
    const callback = buildShopifyOAuthCallback(searchParams);
    if (!callback) {
      captureException(new Error("Missing required Shopify OAuth parameters"), {
        extra: {
          code: searchParams.get("code"),
          shop: searchParams.get("shop"),
          state: searchParams.get("state"),
        },
        tags: {
          integrationType: "SHOPIFY",
        },
      });
      return;
    }
    const callbackKey = searchParams.toString();
    if (processedCallbacksRef.current.has(callbackKey)) {
      return;
    }
    processedCallbacksRef.current.add(callbackKey);
    if (isPending) {
      return;
    }
    mutate(callback);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasMissingParams =
    !searchParams.get("code") ||
    !searchParams.get("shop") ||
    !searchParams.get("state");

  return (
    <Page centered navbar={<Navbar><LanguageToggle /></Navbar>}>
      <ContentWrapper>
        {isError || hasMissingParams ? (
          <>
            <Title>{t("shopify.connectionFailed")}</Title>
            <InfoBox variant="error">
              {hasMissingParams ? t("shopify.unknownError") : errorMessage}
            </InfoBox>
          </>
        ) : (
          <>
            {!isSuccess ? (
              <>
                <Title>{t("shopify.connecting")}</Title>
                <LoadingSpinner centered />
                <Message>{t("shopify.pleaseWait")}</Message>
              </>
            ) : (
              <>
                <Title>{t("shopify.completedTitle")}</Title>
                <InfoBox variant="success">{t("shopify.connectedSuccess")}</InfoBox>
              </>
            )}
          </>
        )}
      </ContentWrapper>
    </Page>
  );
};

export default ShopifyIntegration;
